import React, { useEffect } from 'react'
import { useDispatch } from 'hooks/use-redux'
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import { autologinUser } from '@disco/state-management/actions/UserActions'
import { getIsTrial } from '@disco/state-management/selectors/business'
import { isTaxNotCalculated } from '@disco/state-management/selectors/subscription'
import { isShowPrivacyPolicyDialog } from '@disco/state-management/selectors/user'
import { isTrialExpired } from '@disco/utils/trial'
import { isLoggingInFromAccountsSubdomain } from '@disco/api/utils/auth'

import PlaylistLoadingContainer from 'components/common/playlist-loading-container'
import PlaylistWithTracksLoadingContainer from 'components/common/playlist-loading-container/PlaylistWithTracksLoadingContainer'
import FeatureTableModal from 'components/common/pricing2021/feature-table'
import AddOnsConfirmModal from 'components/common/subscribed-user-billing/add-ons/confirm-modal'
import ThirdPartyRightsData from 'components/common/tracks/metadata-fields/rights-data/third-party-rights-data'
import ArtistVerificationModal from 'components/modals/artist-verification-modal'
import ArtistVerificationApplyModal from 'components/modals/artist-verification-modal/apply'
import DiscoverMusicVideoModal from 'components/modals/discover-music-video-modal'
import DiscoverySuiteModal from 'components/modals/discovery-suite-modal'
import FindArtistModal from 'components/modals/find-artist'
import AudioSeparationTermsDialog from 'components/modals/audio-separation-terms-dialog'
import LyricTranscriptionTermsDialog from 'components/modals/lyric-transcription-terms-dialog'
import NeedMoreUsersOrTracksModal from 'components/modals/need-more-users-or-tracks-modal'

import OverQuotaPlanModal from 'components/modals/over-quota-plan-modal'
import PlaylistPresentationSocialLinksShortlistContainer from 'components/modals/playlist-presentation-social-links/PlaylistPresentationSocialLinksShortlistContainer'
import PrivacyPolicyDialog from 'components/modals/privacy-policy-dialog'
import TrackLeakTracing from 'components/modals/track-leak-tracing'
import WatermarkPlaylistModal from 'components/modals/watermark-playlist-modal'

import AppWrapper from 'containers/AppWrapper'

import ArtistOpportunities from 'pages/artist-opportunities'
import BulkUploader from 'pages/bulk-uploader'
import CampaignsPage from 'pages/campaigns'
import CampaignDetailsPage from 'pages/campaigns/campaign-details'
import CampaignListPage from 'pages/campaigns/campaign-list'
import DraftCampaignListPage from 'pages/campaigns/campaign-list/draft'
import CampaignListLoader from 'pages/campaigns/campaign-list/loader'
import ScheduledCampaignListPage from 'pages/campaigns/campaign-list/scheduled'
import SentCampaignListPage from 'pages/campaigns/campaign-list/sent'
import CampaignMasterPage from 'pages/campaigns/campaign-master'
import CampaignStatsPage from 'pages/campaigns/campaign-stats'
import CampaignSummaryPage from 'pages/campaigns/campaign-stats/campaign-summary'
import CampaignUsersPage from 'pages/campaigns/campaign-stats/campaign-users'
import CampaignChooseTemplatePage from 'pages/campaigns/choose-template'
import CampaignContentEditorPage from 'pages/campaigns/editor'
import CampaignPreviewPage from 'pages/campaigns/preview'
import CampaignRecipientsPage from 'pages/campaigns/recipients'
import EmailSchedulerPage from 'pages/campaigns/schedule-later'
import ChannelAcceptInvite from 'pages/channel/accept-invite'
import ContactSales from 'pages/contact-sales'
import ContactSupport from 'pages/contact-support'
// contacts
import Contacts from 'pages/contacts'
import ContactsAdd from 'pages/contacts/add'
import ContactActivity from 'pages/contacts/contact-activity'
import ContactDelete from 'pages/contacts/contact-delete'
import ContactDetails from 'pages/contacts/contact-details'
import DeferredContactImportSuccessDialog from 'pages/contacts/deferred-import-success'
import ContactsImport from 'pages/contacts/import'
import ContactsImportDialog from 'pages/contacts/import-dialog'
import ContactImportSuccessDialog from 'pages/contacts/import-success'
import ContactListDelete from 'pages/contacts/list-delete'
import ContactsMailingListEdit from 'pages/contacts/mailing-list-edit'
import ContactsMailingListRecipients from 'pages/contacts/mailing-list-recipients'
import ContactsMailingLists from 'pages/contacts/mailing-lists'
import CopyMetadata from 'pages/copy-metadata'
import { DataManagementNesting } from 'pages/data-management'
// demo
import Demo from 'pages/demo'
import ExpiredSubscription from 'pages/expired-subscription'
import ExplorerArtistsArtist from 'pages/explorer/artists/artist'
import ExplorerArtistsArtistLinkedArtists from 'pages/explorer/artists/artist/linked-artists'
import ExplorerArtistsArtistPages from 'pages/explorer/artists/artist/pages'
import ExplorerArtistsArtistProfile from 'pages/explorer/artists/artist/profile'
// explorer channel pages
import ChannelChecker from 'pages/explorer/channels/checker'
import ChannelDelete from 'pages/explorer/channels/delete'
import ChannelShareRevocation from 'pages/explorer/channels/revoke-share'
import ChannelShare from 'pages/explorer/channels/share'
import FileImport from 'pages/explorer/file-import'
// inbox pages
import ImportStemsModal from 'pages/explorer/inboxes/import-stems'
import InboxPlaylistRemove from 'pages/explorer/inboxes/playlist-remove'
import InboxSelectionRemove from 'pages/explorer/inboxes/selection-remove'
import InboxTrackRemove from 'pages/explorer/inboxes/track-remove'
import PlaylistAddToChannel from 'pages/explorer/playlists/add-to-channel'
import PlaylistAddToNewChannel from 'pages/explorer/playlists/add-to-new-channel'
import PlaylistDelete from 'pages/explorer/playlists/delete'
import PlaylistEditMetadata from 'pages/explorer/playlists/edit-metadata'
// playlist pages
import PlaylistStats from 'pages/explorer/playlists/playlist-stats'
import PlaylistRemoveFromPrivateLibrary from 'pages/explorer/playlists/remove-from-library'
import PlaylistShare from 'pages/explorer/playlists/share'
import PlaylistShared from 'pages/explorer/playlists/shared'
import PlaylistCreateEmailGroup from 'pages/explorer/playlists/shared/create-email-group'
import GroupLink from 'pages/explorer/redirects/group-link'
import GroupPlaylistRedirect from 'pages/explorer/redirects/group-playlist-redirect'
import GroupRedirect from 'pages/explorer/redirects/group-redirect'
import PlaylistRedirect from 'pages/explorer/redirects/playlist-redirect'
// explorer pages
import TrackRedirect from 'pages/explorer/redirects/track-redirect'
import ExplorerAddToChannel from 'pages/explorer/selection/add-to-channel'
import AddToShortlist from 'pages/explorer/selection/add-to-shortlist'
import ExplorerBulkEdit from 'pages/explorer/selection/bulk-edit'
import ExplorerBulkEditSelection from 'pages/explorer/selection/bulk-edit/selection'
// selection
import SelectionChecker from 'pages/explorer/selection/checker'
import TrackMergeConfirmation from 'pages/explorer/selection/confirm-merge'
import SelectionDelete from 'pages/explorer/selection/delete'
import TrackMergeDisable from 'pages/explorer/selection/disable-merge'
import TrackMerge from 'pages/explorer/selection/merge'
import ExplorerRemoveFromChannel from 'pages/explorer/selection/remove-from-channel'
import ShortlistAddToChannel from 'pages/explorer/shortlists/add-to-channel'
import ShortlistBulkEdit from 'pages/explorer/shortlists/bulk-edit'
import ShortlistsClear from 'pages/explorer/shortlists/clear'
import ShortlistsClose from 'pages/explorer/shortlists/close'
import ShortlistEditSection from 'pages/explorer/shortlists/edit-section'
import ShortlistsFileImport from 'pages/explorer/shortlists/file-import'
import ShortlistsReset from 'pages/explorer/shortlists/reset'
import ShortlistMetadataReset from 'pages/explorer/shortlists/save/playlist-metadata-reset'
import PlaylistSaveModal from 'pages/explorer/shortlists/save/playlist-save-modal'
// disabled: webinars are on hold
// https://app.asana.com/0/35126173840535/817668258551664/f
// import Webinar from 'pages/help/webinar'
// shortlist pages
import {
  METADATA_TAB_IDX,
  PRESENTATION_TAB_IDX,
  SAVE_TAB_IDX,
} from 'pages/explorer/shortlists/save/playlist-save-modal/constants'
import ShortlistThemeSelect from 'pages/explorer/shortlists/save/playlist-theme-select'
import ShortlistSaved from 'pages/explorer/shortlists/saved'
import ShortlistShare from 'pages/explorer/shortlists/share'
import ShortlistShared from 'pages/explorer/shortlists/shared'
import CreateEmailGroup from 'pages/explorer/shortlists/shared/create-email-group'
import ShortlistTrackOrder from 'pages/explorer/shortlists/track-order'
// track pages
import TrackShared from 'pages/explorer/tracks/shared'
import ShareWarning from 'pages/explorer/warnings/share-warning'
import ForgotPassword from 'pages/forgot-password'
import GetBulkUploader from 'pages/get-bulk-uploader'
import Home from 'pages/Home'
import Insights, {
  InsightsRecipients,
  InsightsRecipientsPlaylistsDownloads,
  InsightsRecipientsPlaylistsViews,
  InsightsRecipientsTracksDownloads,
  InsightsRecipientsTracksPlaybacks,
  InsightsRecipientsUsersActivity,
  InsightsReports,
  InsightsTeam,
  InsightsTeamTracksPitches,
  InsightsTeamTracksPlaybacks,
  InsightsTeamUsersPlaylists,
} from 'pages/insights'
import InstructionsSent from 'pages/instructions-sent'
import Invites from 'pages/invites'
import Libraries from 'pages/libraries'
import LibrariesArtists from 'pages/libraries/artists'
import LibrariesArtistsArtist from 'pages/libraries/artists/artist'
import LibrariesArtistsArtistLinkedArtists from 'pages/libraries/artists/artist/linked-artists'
import LibrariesArtistsArtistProfile from 'pages/libraries/artists/artist/profile'
import LibrariesBrowseLibraries from 'pages/libraries/browse-libraries/BrowseLibrariesContainer'
import LibrariesBrowseLibrariesSearch from 'pages/libraries/browse-libraries/BrowseLibrariesSearchContainer'
import LibrariesContentView from 'pages/libraries/browse-libraries/content-view/ContentViewContainer'
import LibrariesCreate from 'pages/libraries/create'
import LibrariesLibrary from 'pages/libraries/library'
import LibrariesLibraryAccess from 'pages/libraries/library/access'
import LibrariesLibraryAlbums from 'pages/libraries/library/albums'
import LibrariesLibraryAlbumsAdd from 'pages/libraries/library/albums/add'
import LibrariesLibraryAlbumsAlbum from 'pages/libraries/library/albums/album'
import LibrariesLibraryArtists from 'pages/libraries/library/artists'
import LibrariesLibraryArtistsArtist from 'pages/libraries/library/artists/artist'
import LibrariesLibraryArtistsArtistFeaturedTracks from 'pages/libraries/library/artists/artist/featured-tracks'
import LibrariesLibraryClients from 'pages/libraries/library/clients'
import LibrariesLibraryClientsApproveRequest from 'pages/libraries/library/clients/approve-request'
import LibrariesLibraryClientsInvite from 'pages/libraries/library/clients/invite'
import LibrariesLibraryContact from 'pages/libraries/library/contact'
import LibrariesLibraryContent from 'pages/libraries/library/content'
import LibrariesLibraryDesign from 'pages/libraries/library/design'
import LibrariesLibraryNotifications from 'pages/libraries/library/notifications'
import LibrariesLibraryPlaylists from 'pages/libraries/library/playlists'
import LibrariesLibraryPlaylistsAdd from 'pages/libraries/library/playlists/add'
import LibrariesLibraryPlaylistsPlaylist from 'pages/libraries/library/playlists/playlist'
import LibrariesLibraryRename from 'pages/libraries/library/rename'
import LibrariesLibraryLicenseRequests from 'pages/libraries/library/license-requests'
import LibrariesLibrarySettings from 'pages/libraries/library/settings'
import LibrariesLibraryStats from 'pages/libraries/library/stats'
import LibrariesLibraryTracks from 'pages/libraries/library/tracks'
import LibrariesList from 'pages/libraries/list'
import Login from 'pages/login'
// Login/Logout
import Logout from 'pages/Logout'
import MfaSetup from 'pages/mfa-setup'
import NotFound from 'pages/NotFound'
import PasswordReset from 'pages/password-reset'
import PasswordWelcome from 'pages/password-welcome'
import CopyPlaylistToChannel from 'pages/playlists/copy-to-channel'
import PostInvites from 'pages/post-invites'
// pricing
import Pricing from 'pages/pricing'
import ProfileDeleted from 'pages/profile-deleted'
// referral program
import {
  ReferralInviteSend,
  ReferralInviteThankYou,
} from 'pages/referral-invite'
import SamlWelcome from 'pages/saml-welcome'
// search
import Search from 'pages/search'
import TagFilter from 'pages/search/tags-filter'
// settings pages
import Settings from 'pages/settings'
import AccountSettings from 'pages/settings/account'
import Billing from 'pages/settings/billing'
import BillingPlans from 'pages/settings/billing/plans'
import SeatsAndTracks from 'pages/settings/billing/seats-and-tracks'
import PlanChangeSummary from 'pages/settings/billing/summary'
import BillingUpdate from 'pages/settings/billing/update'
import BillingUpdateError from 'pages/settings/billing/update-error'
import BillingUpdateSuccess from 'pages/settings/billing/update-success'
import BusinessSettings from 'pages/settings/business'
import ChannelsSettings from 'pages/settings/channels'
import DDEXSettings from 'pages/settings/ddex'
import DisplaySettings from 'pages/settings/display'
import InboxesSettings from 'pages/settings/inboxes'
import MetadataSettings from 'pages/settings/metadata'
import PlaylistTagsSettings from 'pages/settings/playlist-tags'
import SecuritySettings from 'pages/settings/security'
import ThemesSettings from 'pages/settings/themes'
import TrackTagsSettings from 'pages/settings/track-tags'
import UsersSettings from 'pages/settings/users'
import AddToChannel from 'pages/tracks/add-to-channel'
import AddToNewChannel from 'pages/tracks/add-to-new-channel'
import AddToPlaylist from 'pages/tracks/add-to-playlist'
import AddTrackFormat from 'pages/tracks/add-track-format'
import CopyToChannel from 'pages/tracks/copy-to-channel'
import CopyToPlaylist from 'pages/tracks/copy-to-playlist'
import DeleteTrack from 'pages/tracks/delete'
import DeleteTrackFormat from 'pages/tracks/delete-track-format'
import EditMetadata from 'pages/tracks/edit-metadata'
import InstantInstrumental from 'pages/tracks/instant-instrumental'
import ManageNesting from 'pages/tracks/manage-nesting'
import ManageTrackFormats from 'pages/tracks/manage-track-formats'
import NestingValidation from 'pages/tracks/nesting-validation'
import PreviewMetadata from 'pages/tracks/preview-metadata'
import ReplaceTrackFormat from 'pages/tracks/replace-track-format'
import TrackShare from 'pages/tracks/share'
import TrackStats from 'pages/tracks/stats'
import PlaylistTrackOrder from 'pages/tracks/track-order'
import UpdateBillingInformationModal from 'pages/update-billing-information-modal'
// upload pages
import Upload from 'pages/upload'
import UploadAuth from 'pages/upload/auth'
import UploadSelectPlaylist from 'pages/upload/select-playlist'
// watermarking
import Watermarking from 'pages/watermarking'
import WatermarkingLeakTracing from 'pages/watermarking/leak-tracing'
import WatermarkingPlaylists from 'pages/watermarking/playlists'
import RemoveWatermarking from 'pages/watermarking/playlists/remove-watermarking'

import { BriefDeletePage } from 'features/briefs/pages/brief-delete'
import { BriefsPage } from 'features/briefs/pages/brief-editor'
import { BriefEditorConfirmPage } from 'features/briefs/pages/brief-editor/confirm'
import { BriefEditorContentPage } from 'features/briefs/pages/brief-editor/content'
import { BriefEditorEmailPage } from 'features/briefs/pages/brief-editor/email'
import { BriefEditorNewPage } from 'features/briefs/pages/brief-editor/new'
import { BriefEditorPreviewPage } from 'features/briefs/pages/brief-editor/preview'
import { BriefEditorRecipientsPage } from 'features/briefs/pages/brief-editor/recipients'
import { BriefEditorV2Page } from 'features/briefs/pages/brief-editor-v2'
import { BriefExplorerPage } from 'features/briefs/pages/brief-explorer'
import { BriefManageAccess } from 'features/briefs/pages/brief-manage-access'
import { BriefProjectExplorerPage } from 'features/briefs/pages/brief-project-explorer'
import { BriefSharePage } from 'features/briefs/pages/brief-share'
import { BriefSettingsPage } from 'features/briefs/pages/brief-settings'
import Briefs from 'features/briefs/pages/submission'
import { CreateProModal } from 'features/rights-data/pages/create-pro'
import { CreatePublisherModal } from 'features/rights-data/pages/create-publisher'
import { CreateWriterModal } from 'features/rights-data/pages/create-writer'
import { EditProModal } from 'features/rights-data/pages/edit-pro'
import { EditPublisherModal } from 'features/rights-data/pages/edit-publisher'
import { EditWriterModal } from 'features/rights-data/pages/edit-writer'

import TrackTag from './pages/tracks/tags'

import Signup from './pages/signup'
import CreateRightsholderModal from 'features/rights-data/pages/create-rightsholder'
import CreateLabelModal from 'features/rights-data/pages/create-label'
import EditLabelModal from 'features/rights-data/pages/edit-label'
import EditRightsholderModal from 'features/rights-data/pages/edit-rightsholder'

interface ProtectedRouteProps {
  children: React.ReactNode
  store: any
}

// Wrapper component that provides location and params to components that need them
function withLocationAndParams<T extends { location?: any; params?: any }>(
  Component: React.ComponentType<T>,
) {
  return function WithLocationAndParamsWrapper(
    props: Omit<T, 'location' | 'params'>,
  ) {
    const location = useLocation()
    const params = useParams()
    return <Component {...(props as T)} location={location} params={params} />
  }
}

const MfaSetupWrapper = withLocationAndParams(MfaSetup)

// Wrapper components for migrated components
function PlaylistShareWrapper() {
  const location = useLocation()
  return <PlaylistShare location={location} />
}

function PlaylistStatsWrapper() {
  const location = useLocation()
  const params = useParams<{ playlistId: string }>()
  return (
    <PlaylistStats
      location={location}
      params={params as { playlistId: string }}
    />
  )
}

function PlaylistTrackOrderWrapper() {
  const params = useParams()
  const navigate = useNavigate()
  return <PlaylistTrackOrder navigate={navigate} params={params} />
}

function ShortlistShareWrapper() {
  return <ShortlistShare />
}

function ShareWarningWrapper() {
  const params = useParams()
  return <ShareWarning params={params} />
}

function TagFilterWrapper() {
  const navigate = useNavigate()
  return <TagFilter navigate={navigate} />
}

function TrackSharedWrapper() {
  const location = useLocation()
  const params = useParams()
  return <TrackShared location={location} params={params} />
}

function SettingsWrapper() {
  const location = useLocation()
  return (
    <Settings location={location}>
      <Outlet />
    </Settings>
  )
}

function PlanChangeSummaryWrapper() {
  const location = useLocation()
  return (
    <PlanChangeSummary location={location}>
      <Outlet />
    </PlanChangeSummary>
  )
}

function LibrariesBrowseLibrariesWrapper() {
  const location = useLocation()
  return (
    <LibrariesBrowseLibraries location={location}>
      <Outlet />
    </LibrariesBrowseLibraries>
  )
}

function LibrariesWrapper() {
  const location = useLocation()
  return <Libraries location={location} />
}

function LibrariesLibraryArtistsArtistWrapper() {
  const location = useLocation()
  return (
    <LibrariesLibraryArtistsArtist location={location}>
      <Outlet />
    </LibrariesLibraryArtistsArtist>
  )
}

function LibrariesLibraryClientsWrapper() {
  const location = useLocation()
  return (
    <LibrariesLibraryClients location={location}>
      <Outlet />
    </LibrariesLibraryClients>
  )
}

function LibrariesLibraryClientsApproveRequestWrapper() {
  const location = useLocation()
  return <LibrariesLibraryClientsApproveRequest location={location} />
}

function LibrariesLibraryClientsInviteWrapper() {
  const location = useLocation()
  return <LibrariesLibraryClientsInvite location={location} />
}

function PlaylistLoadingContainerWrapper() {
  return (
    <PlaylistLoadingContainer>
      <Outlet />
    </PlaylistLoadingContainer>
  )
}

function LibrariesLibraryStatsWrapper() {
  const location = useLocation()
  return <LibrariesLibraryStats location={location} />
}

function BriefsWrapper() {
  const location = useLocation()
  return (
    <Briefs location={location}>
      <Outlet />
    </Briefs>
  )
}

function ContactImportSuccessDialogWrapper() {
  const params = useParams()
  return <ContactImportSuccessDialog params={params} />
}

function CampaignStatsPageWrapper({
  children,
}: {
  children?: React.ReactNode
}) {
  return <CampaignStatsPage>{children || <Outlet />}</CampaignStatsPage>
}

function WatermarkingPlaylistsWrapper() {
  const location = useLocation()
  return (
    <WatermarkingPlaylists location={location}>
      <Outlet />
    </WatermarkingPlaylists>
  )
}

const WildcardRedirect = ({ to }: { to: string }) => {
  const { '*': wildcard } = useParams()
  const { search } = useLocation()

  let url = to
  if (to.endsWith('*')) url = to.replace('*', wildcard + search)
  return <Navigate to={url} replace />
}

/**
 * Redirect that preserves location.state.
 * Useful for redirecting old routes to new ones while keeping any in-flight
 * navigation state (e.g., trackId, returnTo, action).
 */
const StatePreservingRedirect = ({ to }: { to: string }) => {
  const location = useLocation()
  return <Navigate to={to} state={location.state} replace />
}

// Specific wrapper components for components that need location/params/route props

function ProtectedRoute({ children, store }: ProtectedRouteProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const state = store.getState()
  const { user } = state
  const isTrial = getIsTrial(state)
  const isLoginRoute = location.pathname === '/login' // render <Login> because it contains data loading action

  useEffect(() => {
    // Auth check
    if (!user.loggedIn) {
      const originalUrl = location.pathname + location.search
      dispatch(autologinUser(originalUrl))
      const loginUrl =
        location.pathname === '/'
          ? '/login'
          : `/login?return=${encodeURIComponent(originalUrl)}`
      navigate(loginUrl)
      return
    }

    // Subscription checks
    if (
      user &&
      user.loggedIn &&
      user.business &&
      user.business.private_metadata
    ) {
      if (location.pathname === '/settings/billing/update') {
        return
      }

      if (isTrialExpired(user) && location.pathname !== '/pricing') {
        navigate('/pricing')
        return
      }

      if (
        user.business.private_metadata.expired_subscription &&
        location.pathname !== '/expired-subscription'
      ) {
        navigate('/expired-subscription')
        return
      }

      if (
        !isTrial &&
        location.pathname.startsWith('/settings/billing/artist-signup')
      ) {
        navigate('/settings/billing')
        return
      }

      if (
        isShowPrivacyPolicyDialog(state) &&
        location.pathname !== '/privacy-policy'
      ) {
        navigate('/privacy-policy')
        return
      }
    }

    const REDIRECT_ID = 'redirect_to_update_billing_information'
    const BILLING_PATH = '/settings/billing/update-billing-information-modal'
    const taxNotCalculated = isTaxNotCalculated(state)
    const pathCondition = location.pathname !== BILLING_PATH

    if (pathCondition && taxNotCalculated) {
      const DAY_IN_MS = 24 * 60 * 60 * 1000
      const lastRedirectTime = localStorage.getItem(REDIRECT_ID)

      if (lastRedirectTime) {
        const lastRedirectTimeInMs = new Date(lastRedirectTime).getTime()
        const dateNowInMs = Date.now()

        if (dateNowInMs - lastRedirectTimeInMs < DAY_IN_MS) {
          return
        }
      }

      localStorage.setItem(REDIRECT_ID, new Date().toISOString())
      navigate(BILLING_PATH)
      return
    }
  }, [location, navigate, dispatch, user, isTrial, state])

  if (isLoggingInFromAccountsSubdomain()) {
    // accounts page can only redirect user to business hostname after login
    return <span>Redirecting...</span>
  }
  if (!user.loggedIn && !isLoginRoute) {
    // avoid rendering app with null data during navigate('/login')
    return null
  }

  return <>{children}</>
}

// Rights Data Contact Modals - nested routes for EditMetadata pages
const rightsDataContactModalRoutes = (
  <>
    <Route path="rights-data/writers/new" element={<CreateWriterModal />} />
    <Route
      path="rights-data/writers/:writerId/edit"
      element={<EditWriterModal />}
    />
    <Route
      path="rights-data/publishers/new"
      element={<CreatePublisherModal />}
    />
    <Route
      path="rights-data/publishers/:publisherId/edit"
      element={<EditPublisherModal />}
    />
    <Route path="rights-data/pros/new" element={<CreateProModal />} />
    <Route
      path="rights-data/rightsholders/new"
      element={<CreateRightsholderModal />}
    />
    <Route path="rights-data/labels/new" element={<CreateLabelModal />} />
    <Route path="rights-data/pros/:proId/edit" element={<EditProModal />} />
    <Route
      path="rights-data/labels/:labelId/edit"
      element={<EditLabelModal />}
    />
    <Route
      path="rights-data/rightsholders/:rightsholderId/edit"
      element={<EditRightsholderModal />}
    />
  </>
)

function AppRoutes({ store }: { store: any }) {
  const location = useLocation()
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute store={store}>
            <AppWrapper location={location}>
              <Outlet />
            </AppWrapper>
          </ProtectedRoute>
        }
      >
        <Route index element={null} />
        <Route path="/privacy-policy" element={<PrivacyPolicyDialog />} />
        <Route path="/mfa-setup" element={<MfaSetupWrapper />} />
        <Route path="/file-import" element={<FileImport />} />
        <Route
          path="/shortlists/:shortlistId/tracks/:trackIndex/edit-metadata"
          element={<EditMetadata />}
        >
          {rightsDataContactModalRoutes}
        </Route>
        <Route
          path="/shortlists/:shortlistId/save"
          element={<PlaylistSaveModal tabIndex={SAVE_TAB_IDX} />}
        >
          <Route path="themes" element={<div />} />
        </Route>

        <Route
          path="/shortlists/:shortlistId/metadata"
          element={<PlaylistSaveModal tabIndex={METADATA_TAB_IDX} />}
        >
          <Route path="reset" element={<ShortlistMetadataReset />} />
        </Route>
        <Route
          path="/shortlists/:shortlistId/save/presentation"
          element={<PlaylistSaveModal tabIndex={PRESENTATION_TAB_IDX} />}
        >
          <Route
            path="social-links"
            element={<PlaylistPresentationSocialLinksShortlistContainer />}
          />
          <Route path="find-artist" element={<FindArtistModal />} />
        </Route>
        <Route
          path="/shortlists/:shortlistId/saved"
          element={<ShortlistSaved />}
        />
        <Route
          path="/shortlists/:shortlistId/share-warning"
          element={<ShareWarning />}
        />
        <Route
          path="/shortlists/:shortlistId/share"
          element={<ShortlistShareWrapper />}
        />
        <Route
          path="/shortlists/:shortlistId/shared/:shareId"
          element={<ShortlistShared />}
        />
        <Route
          path="/shortlists/:shortlistId/shared/:shareId/create-email-group"
          element={<CreateEmailGroup />}
        />
        <Route
          path="/shortlists/:shortlistId/add-to-channel"
          element={<ShortlistAddToChannel />}
        />
        <Route
          path="/shortlists/:shortlistId/bulk-edit"
          element={<ShortlistBulkEdit />}
        >
          {rightsDataContactModalRoutes}
        </Route>
        <Route
          path="/shortlists/:shortlistId/track-order"
          element={<ShortlistTrackOrder />}
        />
        <Route
          path="/shortlists/:shortlistId/sections/add"
          element={<ShortlistEditSection />}
        />
        <Route
          path="/shortlists/:shortlistId/delete"
          element={<ShortlistsClear />}
        />
        <Route
          path="/shortlists/:shortlistId/close"
          element={<ShortlistsClose />}
        />
        <Route
          path="/shortlists/:shortlistId/file-import"
          element={<ShortlistsFileImport />}
        />
        <Route path="/shortlists/confirm-reset" element={<ShortlistsReset />} />
        <Route
          path="/selection"
          element={
            <SelectionChecker>
              <Outlet />
            </SelectionChecker>
          }
        >
          <Route path="add-to-channel" element={<ExplorerAddToChannel />} />
          <Route
            path="remove-from-channel"
            element={<ExplorerRemoveFromChannel />}
          />
          <Route path="bulk-edit" element={<ExplorerBulkEditSelection />}>
            {rightsDataContactModalRoutes}
          </Route>
          <Route path="add-to-shortlist" element={<AddToShortlist />} />
          <Route path="delete" element={<SelectionDelete />} />
          <Route path="merge" element={<TrackMerge />} />
          <Route path="confirm-merge" element={<TrackMergeConfirmation />} />
          <Route path="merge-disabled" element={<TrackMergeDisable />} />
        </Route>
        <Route
          path="/explorer/artists/:artistId"
          element={<ExplorerArtistsArtist />}
        >
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ExplorerArtistsArtistProfile />} />
          <Route
            path="linked-artists"
            element={<ExplorerArtistsArtistLinkedArtists />}
          />
          <Route path="pages" element={<ExplorerArtistsArtistPages />} />
          <Route path="showcases" element={<Navigate to="pages" replace />} />
        </Route>
        {/* Outside of the Channel Checker */}
        <Route
          path="/explorer/channels/:channelId/join"
          element={<ChannelAcceptInvite />}
        />
        <Route
          path="/explorer/channels/:channelId"
          element={<ChannelChecker />}
        >
          <Route path="share" element={<ChannelShare />} />
          <Route path="share-warning" element={<ShareWarningWrapper />} />
          <Route path="delete" element={<ChannelDelete />} />
          <Route path="revoke-share" element={<ChannelShareRevocation />} />
        </Route>
        <Route path="/explorer/tracks/:trackId" element={<TrackRedirect />} />
        <Route
          path="/explorer/tracks/:trackId/share"
          element={<TrackShare />}
        />
        <Route
          path="/explorer/tracks/:trackId/share/:sourceLibraryId"
          element={<TrackShare />}
        />
        <Route
          path="/explorer/tracks/:trackId/stats"
          element={<TrackStats />}
        />
        <Route path="/explorer/tracks/:trackId/tags" element={<TrackTag />} />
        <Route path="/explorer/bulk-uploader" element={<BulkUploader />} />
        <Route
          path="/explorer/get-bulk-uploader"
          element={<GetBulkUploader />}
        />
        <Route path="/explorer/copy-metadata" element={<CopyMetadata />} />
        <Route
          path="/explorer/audio-separation-terms-dialog"
          element={<AudioSeparationTermsDialog />}
        />
        {/* Redirect from old route to new unified audio separation dialog, preserving state */}
        <Route
          path="/explorer/instant-instrumental-terms-dialog"
          element={
            <StatePreservingRedirect to="/explorer/audio-separation-terms-dialog" />
          }
        />
        <Route
          path="/explorer/:trackId/lyric-transcription-terms-dialog"
          element={<LyricTranscriptionTermsDialog />}
        />
        <Route
          path="/explorer/tracks/:trackId/instant-instrumental"
          element={<InstantInstrumental />}
        />
        <Route
          path="/explorer/discovery-suite"
          element={<DiscoverySuiteModal />}
        />
        <Route path="add-ons-confirm-modal" element={<AddOnsConfirmModal />} />
        <Route
          path="/explorer/tracks/:trackId/formats"
          element={<ManageTrackFormats />}
        />
        <Route
          path="/explorer/tracks/:trackId/manage-nesting"
          element={<ManageNesting />}
        />
        <Route
          path="/explorer/tracks/:trackId/add-format"
          element={<AddTrackFormat />}
        />
        <Route
          path="/explorer/tracks/:trackId/delete-format/:format"
          element={<DeleteTrackFormat />}
        />
        <Route
          path="/explorer/tracks/:trackId/replace-format/:format"
          element={<ReplaceTrackFormat />}
        />
        <Route
          path="/explorer/tracks/:trackId/delete"
          element={<DeleteTrack />}
        />
        <Route
          path="/explorer/tracks/:trackId/add-to-shortlist"
          element={<AddToShortlist />}
        />
        <Route
          path="/explorer/tracks/:trackId/add-to-channel"
          element={<AddToChannel />}
        />
        <Route
          path="/explorer/tracks/:trackId/:originGroupId/copy-to-channel"
          element={<CopyToChannel />}
        />
        <Route
          path="/explorer/tracks/:trackId/add-to-new-channel"
          element={<AddToNewChannel />}
        />
        <Route
          path="/explorer/tracks/:trackId/edit-metadata"
          element={<EditMetadata />}
        >
          {rightsDataContactModalRoutes}
        </Route>
        <Route
          path="/explorer/tracks/:trackId/rights-data"
          element={<ThirdPartyRightsData />}
        />
        <Route
          path="/explorer/tracks/:trackId/preview-metadata"
          element={<PreviewMetadata />}
        />
        <Route
          path="/explorer/tracks/:trackId/add-to-playlist"
          element={<AddToPlaylist />}
        />
        <Route
          path="/explorer/tracks/:trackId/:originGroupId/copy-to-playlist"
          element={<CopyToPlaylist />}
        />
        <Route
          path="/explorer/tracks/:trackId/nesting-validation"
          element={<NestingValidation />}
        />
        <Route
          path="/explorer/playlists/:playlistId"
          element={<PlaylistRedirect />}
        />
        <Route
          path="/explorer/playlists/:playlistId"
          element={<PlaylistWithTracksLoadingContainer />}
        >
          <Route
            path="tracks/:trackIndex/edit-metadata"
            element={<EditMetadata />}
          >
            {rightsDataContactModalRoutes}
          </Route>
        </Route>
        <Route
          path="/explorer/playlists/:playlistId"
          element={<PlaylistLoadingContainer />}
        >
          <Route path="share" element={<PlaylistShareWrapper />} />
          <Route path="share-warning" element={<ShareWarningWrapper />} />
          <Route path="stats" element={<PlaylistStatsWrapper />} />
          <Route path="bulk-edit" element={<ExplorerBulkEdit />}>
            {rightsDataContactModalRoutes}
          </Route>
          <Route path="metadata" element={<PlaylistEditMetadata />} />
          <Route path="add-to-shortlist" element={<AddToShortlist />} />
          <Route path="add-to-channel" element={<PlaylistAddToChannel />} />
          <Route
            path=":originGroupId/copy-to-channel"
            element={<CopyPlaylistToChannel />}
          />
          <Route
            path="add-to-new-channel"
            element={<PlaylistAddToNewChannel />}
          />
          <Route path="delete" element={<PlaylistDelete />} />
          <Route path="track-order" element={<PlaylistTrackOrderWrapper />} />
          <Route
            path="remove-from-library"
            element={<PlaylistRemoveFromPrivateLibrary />}
          />
        </Route>
        <Route
          path="/explorer/playlists/:playlistId/shared/:shareId"
          element={<PlaylistShared {...({} as any)} />}
        />
        <Route
          path="/explorer/tracks/:trackId/shared/:shareId"
          element={<TrackSharedWrapper />}
        />
        <Route
          path="/explorer/playlists/:playlistId/shared/:shareId/create-email-group"
          element={<PlaylistCreateEmailGroup />}
        />
        <Route path="/import-stems" element={<ImportStemsModal />} />
        <Route
          path="/inboxes/:groupId/playlists/:playlistId"
          element={<GroupPlaylistRedirect />}
        />
        <Route
          path="/inboxes/:inboxId/tracks/:trackId/remove"
          element={<InboxTrackRemove />}
        />
        <Route
          path="/inboxes/:inboxId/playlists/:playlistId"
          element={<PlaylistLoadingContainer />}
        >
          <Route path="remove" element={<InboxPlaylistRemove />} />
        </Route>
        <Route
          path="/inboxes/:inboxId/selection/remove"
          element={<InboxSelectionRemove />}
        />
        <Route path="/contact-support" element={<ContactSupport />} />
        <Route path="/contact-sales" element={<ContactSales />} />
        <Route path="/referral-invite/send" element={<ReferralInviteSend />} />
        <Route
          path="/referral-invite/thank-you"
          element={<ReferralInviteThankYou />}
        />
        {/* <Route
         path="/catalogs/:groupId/playlists/:playlistId"
         component={GroupPlaylistRedirect}
        /> */}
        <Route
          path="/channels/:groupId/playlists/:playlistId"
          element={<GroupPlaylistRedirect />}
        />
        <Route path="/inboxes/:groupSlug" element={<GroupRedirect />} />
        {/* <Route path="/catalogs/:groupSlug" component={GroupRedirect} /> */}
        <Route path="/channels/:groupSlug" element={<GroupRedirect />} />
        <Route path="/cl/:groupId" element={<GroupLink />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/pricing" element={<Pricing />}>
          <Route
            path="over-quota-plan-modal"
            element={<OverQuotaPlanModal />}
          />
          <Route
            path="need-more-users-or-tracks-modal"
            element={<NeedMoreUsersOrTracksModal />}
          />
          <Route path="feature-table" element={<FeatureTableModal />} />
          <Route path="artist-signup" element={<ArtistVerificationModal />} />
          <Route
            path="artist-signup/apply"
            element={<ArtistVerificationApplyModal />}
          />
        </Route>
        <Route path="/expired-subscription" element={<ExpiredSubscription />} />
        <Route path="/search/tag-filter" element={<TagFilterWrapper />} />
        {/* <Route path="/webinar" component={Webinar} /> */}
        <Route
          path="/briefs"
          element={
            <Home>
              <Outlet />
            </Home>
          }
        >
          <Route path=":briefId/delete" element={<BriefDeletePage />} />
          <Route path=":briefId" element={<BriefExplorerPage />} />
          <Route
            path="projects/:projectId"
            element={<BriefProjectExplorerPage />}
          />
          <Route
            path="projects/:projectId/:briefId"
            element={<BriefExplorerPage />}
          />
          <Route
            path="projects/:projectId/:briefId/submissions/:playlistId"
            element={<BriefExplorerPage />}
          />
          <Route path=":briefId/share" element={<BriefSharePage />} />
          <Route
            path=":briefId/submissions/:playlistId"
            element={<BriefExplorerPage />}
          />
          <Route path=":briefId/access" element={<BriefManageAccess />} />
          <Route path=":briefId/settings" element={<BriefSettingsPage />} />
        </Route>

        <Route path="/settings" element={<SettingsWrapper />}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={<AccountSettings />} />
          <Route path="security" element={<SecuritySettings />} />
          <Route path="business" element={<BusinessSettings />} />
          <Route path="themes" element={<ThemesSettings />} />
          <Route path="display" element={<DisplaySettings />} />
          <Route path="settings" element={<Navigate to="display" replace />} />
          <Route path="channels" element={<ChannelsSettings />}>
            <Route path=":channelId/share" element={<ChannelShare />} />
          </Route>
          <Route path="inboxes" element={<InboxesSettings />} />
          <Route path="billing" element={<Billing />}>
            <Route path="update" element={<BillingUpdate />} />
            <Route
              path="update-seats-and-tracks"
              element={<SeatsAndTracks />}
            />
            <Route
              path="update-successful"
              element={<BillingUpdateSuccess />}
            />
            <Route path="update-error" element={<BillingUpdateError />} />
            <Route
              path="over-quota-plan-modal"
              element={<OverQuotaPlanModal />}
            />
            <Route
              path="need-more-users-or-tracks-modal"
              element={<NeedMoreUsersOrTracksModal />}
            />
            <Route path="feature-table" element={<FeatureTableModal />} />
            <Route
              path="update-billing-information-modal"
              element={<UpdateBillingInformationModal />}
            />
            <Route
              path="add-ons-confirm-modal"
              element={<AddOnsConfirmModal />}
            />
            <Route path="artist-signup" element={<ArtistVerificationModal />} />
            <Route
              path="artist-signup/apply"
              element={<ArtistVerificationApplyModal />}
            />
          </Route>
          <Route path="billing/plans" element={<BillingPlans />}>
            <Route path="feature-table" element={<FeatureTableModal />} />
            <Route
              path="over-quota-plan-modal"
              element={<OverQuotaPlanModal />}
            />
            <Route
              path="need-more-users-or-tracks-modal"
              element={<NeedMoreUsersOrTracksModal />}
            />
          </Route>
          <Route path="billing/summary" element={<PlanChangeSummaryWrapper />}>
            <Route
              path="update-billing-information-modal"
              element={<UpdateBillingInformationModal />}
            />
            <Route path="update" element={<BillingUpdate />} />
            <Route
              path="update-successful"
              element={<BillingUpdateSuccess />}
            />
            <Route path="update-error" element={<BillingUpdateError />} />
          </Route>
          <Route path="metadata" element={<MetadataSettings />} />
          <Route path="track-tags" element={<TrackTagsSettings />} />
          <Route path="playlist-tags" element={<PlaylistTagsSettings />} />
          <Route path="users" element={<UsersSettings />}>
            <Route path="invites" element={<Invites />} />
          </Route>
          <Route path="team" element={<Navigate to="users" replace />} />
          <Route path="ddex" element={<DDEXSettings />} />
          <Route path="nesting-export" element={<DataManagementNesting />} />
        </Route>
        <Route path="/lists" element={<Contacts />}>
          <Route index element={<ContactsMailingLists />} />
          <Route path="delete-list" element={<ContactsMailingLists />}>
            <Route path=":deleteListId" element={<ContactListDelete />} />
          </Route>
          <Route path="create" element={<ContactsMailingLists />}>
            <Route index element={<ContactsMailingListEdit />} />
          </Route>
          <Route path=":listId/edit" element={<ContactsMailingLists />}>
            <Route index element={<ContactsMailingListEdit />} />
          </Route>
          <Route path=":listId" element={<ContactsMailingListRecipients />}>
            <Route path="add-single" element={<ContactsAdd />} />
            <Route path="file-import" element={<ContactsImportDialog />} />
            <Route path="delete" element={<ContactDelete />} />
            <Route path="delete-list" element={<ContactListDelete />} />
          </Route>
          <Route path=":listId/import" element={<ContactsImport />}>
            <Route path="add" element={<ContactImportSuccessDialogWrapper />} />
            <Route
              path="import-success"
              element={<ContactImportSuccessDialogWrapper />}
            />
            <Route
              path="deferred-import-success"
              element={<DeferredContactImportSuccessDialog />}
            />
          </Route>
        </Route>
        <Route path="/contacts" element={<Contacts />}>
          <Route index element={<Navigate to="list" replace />} />
          <Route path="list" element={<ContactsMailingListRecipients />}>
            <Route path="add-single" element={<ContactsAdd />} />
            <Route path="file-import" element={<ContactsImportDialog />} />
            <Route path="delete" element={<ContactDelete />} />
          </Route>
          <Route path="import" element={<ContactsImport />}>
            <Route path="add" element={<ContactImportSuccessDialogWrapper />} />
            <Route
              path="import-success"
              element={<ContactImportSuccessDialogWrapper />}
            />
            <Route
              path="deferred-import-success"
              element={<DeferredContactImportSuccessDialog />}
            />
          </Route>
          {/* `:contactId` should be last */}
          <Route path=":contactId" element={<ContactDetails />}>
            <Route path="activity" element={<ContactActivity />} />
            <Route path="delete" element={<ContactDelete />} />
          </Route>
        </Route>
        <Route path="/campaigns" element={<CampaignsPage />}>
          <Route index element={<Navigate to="list" replace />} />
          <Route path="list" element={<CampaignListLoader />}>
            <Route index element={<Navigate to="all" replace />} />
            <Route path="all" element={<CampaignListPage />} />
            <Route path="draft" element={<DraftCampaignListPage />} />
            <Route path="scheduled" element={<ScheduledCampaignListPage />} />
            <Route path="sent" element={<SentCampaignListPage />} />
          </Route>

          <Route path=":campaignId" element={<CampaignMasterPage />}>
            <Route path="stats" element={<CampaignStatsPageWrapper />}>
              {/* TODO: Probably remove redirect and permanently change URL */}
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<div />} />
              <Route path="summary" element={<CampaignSummaryPage />} />
              <Route path="users" element={<CampaignUsersPage />} />
            </Route>
            <Route path="details" element={<CampaignDetailsPage />} />
            <Route path="template" element={<CampaignChooseTemplatePage />} />
            <Route path="editor" element={<CampaignContentEditorPage />} />
            <Route path="recipients" element={<CampaignRecipientsPage />} />
            <Route path="confirm" element={<CampaignPreviewPage />}>
              <Route path="schedule-later" element={<EmailSchedulerPage />} />
            </Route>
          </Route>
        </Route>
        <Route path="/brief-editor/new" element={<BriefEditorNewPage />} />
        <Route path="/brief-editor/:briefId" element={<BriefsPage />}>
          <Route index element={<Navigate to="content" replace />} />
          <Route path="content" element={<BriefEditorContentPage />} />
          <Route path="email" element={<BriefEditorEmailPage />} />
          <Route path="recipients" element={<BriefEditorRecipientsPage />} />
          <Route path="confirm" element={<BriefEditorConfirmPage />} />
        </Route>
        <Route path="/brief-editor-v2/:briefId" element={<BriefEditorV2Page />}>
          <Route path="share" element={<BriefSharePage />} />
          <Route path="access" element={<BriefManageAccess />} />
          <Route path="settings" element={<BriefSettingsPage />} />
          <Route path="delete" element={<BriefDeletePage />} />
        </Route>

        {/* ------------------------------------------------------------ */}
        <Route
          path="/invites"
          element={<Navigate to="/settings/users/invites" replace />}
        />
        <Route path="/insights" element={<Insights />}>
          <Route index element={<Navigate to="recipients" replace />} />
          <Route path="recipients" element={<InsightsRecipients />} />
          <Route
            path="recipients/playlists-downloads"
            element={<InsightsRecipientsPlaylistsDownloads />}
          />
          <Route
            path="recipients/playlists-views"
            element={<InsightsRecipientsPlaylistsViews />}
          />
          <Route
            path="recipients/tracks-downloads"
            element={<InsightsRecipientsTracksDownloads />}
          />
          <Route
            path="recipients/tracks-playbacks"
            element={<InsightsRecipientsTracksPlaybacks />}
          />
          <Route
            path="recipients/users-activity"
            element={<InsightsRecipientsUsersActivity />}
          />
          <Route path="reports" element={<InsightsReports />} />
          <Route path="team" element={<InsightsTeam />}>
            <Route
              path="tracks-pitches"
              element={<InsightsTeamTracksPitches />}
            />
            <Route
              path="tracks-playbacks"
              element={<InsightsTeamTracksPlaybacks />}
            />
            <Route
              path="users-playlists"
              element={<InsightsTeamUsersPlaylists />}
            />
          </Route>
        </Route>
        <Route
          path="/analytics/end-user"
          element={<Navigate to="/insights/recipients" replace />}
        />
        <Route
          path="/analytics/team"
          element={<Navigate to="/insights/team" replace />}
        />
        <Route
          path="/reports"
          element={<Navigate to="/insights/reports" replace />}
        />

        <Route
          path="/product-updates"
          element={
            <div>
              {(() => {
                // window.location.replace('https://www.disco.ac/changelog')
                return null
              })()}
            </div>
          }
        />

        <Route
          path="/browse-libraries"
          element={<Navigate to="/discover" replace />}
        />
        <Route
          path="/browse-libraries/*"
          element={<WildcardRedirect to="/discover/*" />}
        />
        <Route path="/discover" element={<LibrariesBrowseLibrariesWrapper />} />
        <Route
          path="/discover/search"
          element={<LibrariesBrowseLibrariesSearch />}
        />
        <Route path="/discover/playlist" element={<LibrariesContentView />} />
        <Route path="/discover/catalog" element={<LibrariesContentView />} />
        <Route
          path="/libraries"
          element={<Navigate to="/catalogs" replace />}
        />
        <Route
          path="/libraries/*"
          element={<WildcardRedirect to="/catalogs/*" />}
        />
        <Route path="/catalogs" element={<LibrariesWrapper />}>
          <Route index element={<LibrariesList />} />

          <Route
            path="discover-music-video"
            element={<DiscoverMusicVideoModal />}
          >
            <Route index element={<LibrariesList />} />
          </Route>
          <Route path="artists" element={<LibrariesArtists />}>
            <Route path=":artistId" element={<LibrariesArtistsArtist />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route
                path="profile"
                element={<LibrariesArtistsArtistProfile />}
              />
              <Route
                path="related-artists"
                element={<Navigate to="linked-artists" replace />}
              />
              <Route
                path="linked-artists"
                element={<LibrariesArtistsArtistLinkedArtists />}
              />
            </Route>
          </Route>
          <Route path="create" element={<LibrariesCreate />} />
          <Route path=":libraryId" element={<LibrariesLibrary />}>
            <Route index element={<Navigate to="content" replace />} />
            <Route path="access" element={<LibrariesLibraryAccess />} />
            <Route path="albums" element={<LibrariesLibraryAlbums />}>
              <Route path="add" element={<LibrariesLibraryAlbumsAdd />} />
              <Route
                path=":albumId"
                element={<LibrariesLibraryAlbumsAlbum />}
              />
            </Route>
            <Route path="artists" element={<LibrariesLibraryArtists />}>
              <Route
                path=":artistId"
                element={<LibrariesLibraryArtistsArtistWrapper />}
              >
                <Route index element={<Navigate to="profile" replace />} />
                <Route
                  path="profile"
                  element={<LibrariesArtistsArtistProfile />}
                />
                <Route
                  path="featured-tracks"
                  element={<LibrariesLibraryArtistsArtistFeaturedTracks />}
                />
                <Route
                  path="related-artists"
                  element={<Navigate to="linked-artists" replace />}
                />
                <Route
                  path="linked-artists"
                  element={<LibrariesArtistsArtistLinkedArtists />}
                />
              </Route>
            </Route>
            <Route path="clients" element={<LibrariesLibraryClientsWrapper />}>
              <Route
                path="invite"
                element={<LibrariesLibraryClientsInviteWrapper />}
              />
              <Route
                path=":clientId"
                element={<LibrariesLibraryClientsApproveRequestWrapper />}
              />
            </Route>
            <Route path="contact" element={<LibrariesLibraryContact />} />
            <Route path="content" element={<LibrariesLibraryContent />} />
            <Route path="design" element={<LibrariesLibraryDesign />} />
            <Route
              path="notifications"
              element={<LibrariesLibraryNotifications />}
            />
            <Route path="playlists" element={<LibrariesLibraryPlaylists />}>
              <Route path="add" element={<LibrariesLibraryPlaylistsAdd />} />
              <Route
                path=":playlistId"
                element={<PlaylistLoadingContainerWrapper />}
              >
                <Route index element={<LibrariesLibraryPlaylistsPlaylist />} />
              </Route>
            </Route>
            <Route path="rename" element={<LibrariesLibraryRename />} />
            <Route
              path="license_requests"
              element={<LibrariesLibraryLicenseRequests />}
            />
            <Route path="settings" element={<LibrariesLibrarySettings />} />
            <Route path="stats" element={<LibrariesLibraryStatsWrapper />} />
            <Route path="tracks" element={<LibrariesLibraryTracks />} />
          </Route>
        </Route>

        <Route path="/watermarking" element={<Watermarking />}>
          <Route index element={<Navigate to="playlists" replace />} />
          <Route path="playlists" element={<WatermarkingPlaylistsWrapper />}>
            <Route
              path="watermark-playlist"
              element={<WatermarkPlaylistModal />}
            />
          </Route>
          <Route path=":playlistId/" element={<WatermarkingPlaylistsWrapper />}>
            <Route path="stats" element={<PlaylistStatsWrapper />} />
            <Route
              path="remove-watermarking"
              element={<RemoveWatermarking />}
            />
          </Route>
          <Route
            path="watermark-playlist"
            element={<WatermarkPlaylistModal />}
          />
          <Route path="leak-tracing" element={<WatermarkingLeakTracing />}>
            <Route path="tracing" element={<TrackLeakTracing />} />
          </Route>
        </Route>

        <Route path="/artist-opportunities" element={<ArtistOpportunities />} />

        <Route
          path="/data-management"
          element={<Navigate to="/settings/nesting-export" replace />}
        />

        <Route path="/search" element={<Search />}>
          <Route path="track/:trackId/stats" element={<TrackStats />} />
          <Route path="track/:trackId/edit-metadata" element={<EditMetadata />}>
            {rightsDataContactModalRoutes}
          </Route>
          <Route
            path="track/:trackId/rights-data"
            element={<ThirdPartyRightsData />}
          />
          <Route
            path="track/:trackId/add-to-channel"
            element={<AddToChannel />}
          />
          <Route
            path="track/:trackId/add-to-playlist"
            element={<AddToPlaylist />}
          />
          <Route
            path="track/:trackId/add-to-playlist-creator"
            element={<AddToShortlist />}
          />

          {/* Search-nested shortlist save modal routes */}
          <Route
            path="shortlists/:shortlistId/save"
            element={<PlaylistSaveModal tabIndex={SAVE_TAB_IDX} />}
          >
            <Route path="themes" element={<ShortlistThemeSelect />} />
          </Route>
          <Route
            path="shortlists/:shortlistId/metadata"
            element={<PlaylistSaveModal tabIndex={METADATA_TAB_IDX} />}
          >
            <Route path="reset" element={<ShortlistMetadataReset />} />
          </Route>
          <Route
            path="shortlists/:shortlistId/save/presentation"
            element={<PlaylistSaveModal tabIndex={PRESENTATION_TAB_IDX} />}
          >
            <Route
              path="social-links"
              element={<PlaylistPresentationSocialLinksShortlistContainer />}
            />
            <Route path="find-artist" element={<FindArtistModal />} />
          </Route>
          <Route
            path="shortlists/:shortlistId/saved"
            element={<ShortlistSaved />}
          />
          <Route
            path="shortlists/:shortlistId/share-warning"
            element={<ShareWarning />}
          />
          <Route
            path="shortlists/:shortlistId/share"
            element={<ShortlistShare />}
          />

          {/* Search-nested shortlist action routes */}
          <Route
            path="shortlists/:shortlistId/delete"
            element={<ShortlistsClear />}
          />
          <Route
            path="shortlists/:shortlistId/close"
            element={<ShortlistsClose />}
          />
          <Route
            path="shortlists/:shortlistId/bulk-edit"
            element={<ExplorerBulkEdit />}
          >
            {rightsDataContactModalRoutes}
          </Route>
          <Route
            path="shortlists/:shortlistId/sections/add"
            element={<ShortlistEditSection />}
          />
          <Route
            path="shortlists/:shortlistId/track-order"
            element={<ShortlistTrackOrder />}
          />

          {/* Copy-metadata route for search context */}
          <Route path="copy-metadata" element={<CopyMetadata />} />

          {/* Selection routes nested under /search so SearchPage stays mounted */}
          <Route
            path="selection"
            element={
              <SelectionChecker>
                <Outlet />
              </SelectionChecker>
            }
          >
            <Route path="add-to-channel" element={<ExplorerAddToChannel />} />
            <Route
              path="remove-from-channel"
              element={<ExplorerRemoveFromChannel />}
            />
            <Route path="bulk-edit" element={<ExplorerBulkEditSelection />}>
              {rightsDataContactModalRoutes}
            </Route>
            <Route path="add-to-shortlist" element={<AddToShortlist />} />
            <Route path="delete" element={<SelectionDelete />} />
            <Route path="merge" element={<TrackMerge />} />
            <Route path="confirm-merge" element={<TrackMergeConfirmation />} />
            <Route path="merge-disabled" element={<TrackMergeDisable />} />
          </Route>
        </Route>
      </Route>
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/instructions-sent" element={<InstructionsSent />} />
      <Route path="/welcome/:userId/:signature" element={<PasswordWelcome />} />
      <Route
        path="/welcome-saml/:userId/:signature"
        element={<SamlWelcome />}
      />
      <Route
        path="/invitation/:inviteId/:signature"
        element={<PostInvites />}
      />
      <Route
        path="/password-reset/:userId/:signature"
        element={<PasswordReset />}
      />
      <Route path="/upload/:groupId/:groupName" element={<Upload />}>
        <Route path="select-playlist" element={<UploadSelectPlaylist />} />
        <Route path="auth" element={<UploadAuth />} />
      </Route>
      <Route path="/brief/:briefId/:recipientId" element={<BriefsWrapper />}>
        <Route path="select-playlist" element={<UploadSelectPlaylist />} />
        <Route path="auth" element={<UploadAuth />} />
      </Route>
      <Route path="/profile-deleted" element={<ProfileDeleted />} />
      <Route
        path="/brief-editor/:briefId/:inboxId/preview"
        element={<BriefEditorPreviewPage />}
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default function routes(store: any): React.ReactNode {
  return <AppRoutes store={store} />
}
